import React, { useState, useCallback } from 'react';
import { Card, CardHeader, CardBody, Collapse, Badge } from 'reactstrap';
import { ChevronDown, ChevronUp, AlertCircle, Folder } from 'react-feather';
import { H5 } from '../../../../AbstractElements';
import { apiClient } from '../../../../Api/client';
import FilesCollapsible from '../../../Common/FilesCollapsible';

const formatDate = (v) => {
    if (!v) return '-';
    try {
        return new Date(v).toLocaleDateString('tr-TR');
    } catch {
        return '-';
    }
};

/**
 * Belirli bir entity grubu (Çağrı Formları, Kaza Dosyaları, vb.) için üst-collapsible.
 * İçinde her bir entity için ayrı FilesCollapsible render edilir.
 */
const GroupCollapsible = ({ title, count, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggle = () => setIsOpen(prev => !prev);
    if (!count) return null;
    return (
        <Card className="mb-3">
            <CardHeader
                className="cursor-pointer d-flex justify-content-between align-items-center p-3"
                onClick={toggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                style={{ backgroundColor: isOpen ? '#f8f9fa' : '#ffffff', cursor: 'pointer', userSelect: 'none' }}
            >
                <div className="d-flex align-items-center gap-3">
                    <Folder size={18} />
                    <H5 className="mb-0">{title}</H5>
                    <Badge
                        color="secondary"
                        style={{
                            borderRadius: '6px',
                            padding: '0.35em 0.65em',
                            fontSize: '0.85em',
                            fontWeight: 600
                        }}
                    >
                        {count}
                    </Badge>
                </div>
                {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </CardHeader>
            <Collapse isOpen={isOpen}>
                <CardBody>
                    {children}
                </CardBody>
            </Collapse>
        </Card>
    );
};

/**
 * Her kaza (Accident) için bir üst-collapsible.
 * Açıldığında o kazayla ilgili tüm bağlı entity'ler (accidentFile'lar,
 * tahkim alt-entitiesi, icra, masraf, tahsilat, kapak) için sub-FilesCollapsible'lar görünür.
 * Tüm dosyalar salt-okunur.
 */
const AccidentCard = ({ accident, index = 0 }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [accidentFiles, setAccidentFiles] = useState([]);
    const [arbitrations, setArbitrations] = useState([]);
    const [interlocutoryRulings, setInterlocutoryRulings] = useState([]);
    const [expertReports, setExpertReports] = useState([]);
    const [rulings, setRulings] = useState([]);
    const [reapplications, setReapplications] = useState([]);
    const [enforcements, setEnforcements] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [paymentCollections, setPaymentCollections] = useState([]);
    const [finalCompensations, setFinalCompensations] = useState([]);
    const [callForms, setCallForms] = useState([]);

    const extractItems = (resp) => {
        if (!resp) return [];
        if (Array.isArray(resp)) return resp;
        const data = resp.data ?? resp;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    };

    // Referans alanı tek obje / array / string olabilir — her durumu array'e normalize et
    const toRefArray = (ref) => {
        if (!ref) return [];
        return Array.isArray(ref) ? ref : [ref];
    };

    const loadNested = useCallback(async () => {
        if (loaded || loading) return;
        setLoading(true);
        try {
            const afResp = await apiClient.get('/AccidentFile', {
                params: { accident: accident.id, page: 1, pageSize: 100 }
            });
            const afs = extractItems(afResp.data);
            setAccidentFiles(afs);

            const cfIds = [...new Set(afs.flatMap(af => toRefArray(af.callForm).map(cf => typeof cf === 'string' ? cf : cf?.id).filter(Boolean)))];
            const cfObjs = [];
            for (const cfId of cfIds) {
                try {
                    const resp = await apiClient.get(`/CallForm/${cfId}`);
                    const d = resp.data?.data ?? resp.data;
                    if (d?.id) cfObjs.push(d);
                    else cfObjs.push({ id: cfId });
                } catch {
                    cfObjs.push({ id: cfId });
                }
            }
            setCallForms(cfObjs);

            const arbsRaw = afs.flatMap(af => toRefArray(af.arbitration)).filter(Boolean);
            const arbIdsAll = [...new Set(arbsRaw.map(a => typeof a === 'string' ? a : a?.id).filter(Boolean))];
            const arbObjs = [];
            for (const arbId of arbIdsAll) {
                try {
                    const resp = await apiClient.get(`/Arbitration/${arbId}`);
                    const d = resp.data?.data ?? resp.data;
                    if (d?.id) arbObjs.push(d);
                    else arbObjs.push({ id: arbId });
                } catch {
                    arbObjs.push({ id: arbId });
                }
            }
            setArbitrations(arbObjs);

            const arbIds = arbObjs.map(a => a.id).filter(Boolean);

            const fetchFor = async (endpoint, paramName) => {
                const results = [];
                for (const arbId of arbIds) {
                    try {
                        const resp = await apiClient.get(endpoint, {
                            params: { [paramName]: arbId, page: 1, pageSize: 100 }
                        });
                        results.push(...extractItems(resp.data));
                    } catch { /* devam */ }
                }
                return results;
            };

            const [irs, ews, rs, reaps, enfs, comps] = await Promise.all([
                fetchFor('/InterlocutoryRulingInformation', 'arbitrationId'),
                fetchFor('/ExpertWitnessReportInformation', 'arbitrationId'),
                fetchFor('/RulingInformation', 'arbitrationId'),
                fetchFor('/ReapplicationInformation', 'arbitrationId'),
                fetchFor('/Enforcement', 'relatedArbitrationId'),
                fetchFor('/FinalCompensation', 'arbitrationId')
            ]);
            setInterlocutoryRulings(irs);
            setExpertReports(ews);
            setRulings(rs);
            setReapplications(reaps);
            setEnforcements(enfs);
            setFinalCompensations(comps);

            const expensesAll = [];
            const paymentsAll = [];
            for (const af of afs) {
                try {
                    const expResp = await apiClient.get('/expense', { params: { accidentFile: af.id, page: 1, pageSize: 100 } });
                    expensesAll.push(...extractItems(expResp.data));
                } catch { /* devam */ }
                try {
                    const pcResp = await apiClient.get('/paymentcollection', { params: { AccidentFile: af.id, page: 1, pageSize: 100 } });
                    paymentsAll.push(...extractItems(pcResp.data));
                } catch { /* devam */ }
            }
            setExpenses(expensesAll);
            setPaymentCollections(paymentsAll);

            setLoaded(true);
        } finally {
            setLoading(false);
        }
    }, [accident.id, loaded, loading]);

    const toggle = async () => {
        setIsOpen(prev => !prev);
        if (!isOpen) {
            await loadNested();
        }
    };

    const formatTitle = (primary, fallback) => (primary && String(primary).trim()) ? primary : fallback;

    // Referans alanı tek obje / array / string olabilir — her durumu array'e normalize et
    const toIdArray = (ref) => {
        if (!ref) return [];
        const arr = Array.isArray(ref) ? ref : [ref];
        return arr.map(x => typeof x === 'string' ? x : x?.id).filter(Boolean);
    };

    // Reapplication kaydını kaza dosyasına eşlemek için — arbitration.accidentFile üzerinden
    const findAccidentFileForReapplication = (reapplicationId) => {
        const arb = arbitrations.find(a => toIdArray(a.reapplicationInformation).includes(reapplicationId));
        if (!arb) return null;
        const afIds = toIdArray(arb.accidentFile);
        return accidentFiles.find(af => afIds.includes(af.id));
    };

    // Bir tahkimin bağlı olduğu kaza dosyasını bul
    const findAccidentFileForArbitration = (arb) => {
        const afIds = toIdArray(arb.accidentFile);
        return accidentFiles.find(af => afIds.includes(af.id));
    };

    return (
        <Card className="mb-3">
            <CardHeader
                className="cursor-pointer d-flex justify-content-between align-items-center p-3"
                onClick={toggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                style={{ backgroundColor: isOpen ? '#f8f9fa' : '#ffffff', cursor: 'pointer', userSelect: 'none' }}
            >
                <div className="d-flex align-items-center gap-3">
                    <AlertCircle size={18} />
                    <H5 className="mb-0">
                        {formatTitle(accident?.accidentNumber ? `Kaza: ${accident.accidentNumber}` : null, `Kaza #${index + 1}`)}
                    </H5>
                </div>
                {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </CardHeader>
            <Collapse isOpen={isOpen}>
                <CardBody>
                    {loading && <p className="text-center text-muted">Yükleniyor...</p>}
                    {!loading && loaded && (
                        <>
                            <GroupCollapsible title="Çağrı Formları" count={callForms.length}>
                                {callForms.map((cf, i) => (
                                    <FilesCollapsible
                                        key={`cf-${cf.id}`}
                                        title={formatTitle(cf.callFormNumber ? `Çağrı Formu: ${cf.callFormNumber}` : null, `Çağrı Formu Ekleri #${i + 1}`)}
                                        referenceId={cf.id}
                                        callFormReference={true}
                                        canEdit={false}
                                    />
                                ))}
                            </GroupCollapsible>

                            <GroupCollapsible title="Kaza Dosyaları" count={accidentFiles.length}>
                                {accidentFiles.map((af, i) => {
                                    const ftTitle = af.filesToBeWorked?.[0]?.fileType?.[0]?.title || null;
                                    const primary = af.fileNumber
                                        ? (ftTitle ? `Kaza Dosyası: ${af.fileNumber} — ${ftTitle}` : `Kaza Dosyası: ${af.fileNumber}`)
                                        : null;
                                    return (
                                        <FilesCollapsible
                                            key={`af-${af.id}`}
                                            title={formatTitle(primary, `Kaza Dosyası Ekleri #${i + 1}`)}
                                            referenceId={af.id}
                                            canEdit={false}
                                        />
                                    );
                                })}
                            </GroupCollapsible>

                            <GroupCollapsible title="Tahkimler" count={arbitrations.length}>
                                {arbitrations.map((a, i) => {
                                    const cn = a.arbitrationInformation?.caseNumber
                                        || (Array.isArray(a.arbitrationInformation) ? a.arbitrationInformation[0]?.caseNumber : null);
                                    const matchingAf = findAccidentFileForArbitration(a);
                                    const afSuffix = matchingAf?.fileNumber ? ` — Kaza Dosyası: ${matchingAf.fileNumber}` : '';
                                    let primary = null;
                                    if (cn) primary = `Tahkim Dava No: ${cn}${afSuffix}`;
                                    else if (matchingAf?.fileNumber) primary = `Tahkim — Kaza Dosyası: ${matchingAf.fileNumber}`;
                                    return (
                                        <FilesCollapsible
                                            key={`arb-${a.id}`}
                                            title={formatTitle(primary, `Tahkim #${i + 1} Bilgileri Dosyaları`)}
                                            referenceId={a.id}
                                            canEdit={false}
                                        />
                                    );
                                })}
                            </GroupCollapsible>

                            <GroupCollapsible title="Ara Kararlar" count={interlocutoryRulings.length}>
                                {interlocutoryRulings.map((ir, i) => (
                                    <FilesCollapsible
                                        key={`ir-${ir.id}`}
                                        title={formatTitle(ir.arrivalDate ? `Ara Karar: ${formatDate(ir.arrivalDate)}` : null, `Ara Karar #${i + 1} Dosyaları`)}
                                        referenceId={ir.id}
                                        canEdit={false}
                                    />
                                ))}
                            </GroupCollapsible>

                            <GroupCollapsible title="Bilirkişi Raporları" count={expertReports.length}>
                                {expertReports.map((er, i) => (
                                    <FilesCollapsible
                                        key={`ew-${er.id}`}
                                        title={formatTitle(er.arrivalDate ? `Bilirkişi Raporu: ${formatDate(er.arrivalDate)}` : null, `Bilirkişi Raporu #${i + 1} Dosyaları`)}
                                        referenceId={er.id}
                                        canEdit={false}
                                    />
                                ))}
                            </GroupCollapsible>

                            <GroupCollapsible title="Kararlar" count={rulings.length}>
                                {rulings.map((r, i) => (
                                    <FilesCollapsible
                                        key={`r-${r.id}`}
                                        title={formatTitle(r.rulingNumber ? `Karar: ${r.rulingNumber}` : null, `Karar #${i + 1} Dosyaları`)}
                                        referenceId={r.id}
                                        canEdit={false}
                                    />
                                ))}
                            </GroupCollapsible>

                            <GroupCollapsible title="Tekrar Başvurular" count={reapplications.length}>
                                {reapplications.map((re, i) => {
                                    const matchingAf = findAccidentFileForReapplication(re.id);
                                    const title = matchingAf?.fileNumber
                                        ? `Tekrar Başvuru Bilgileri — Kaza Dosyası: ${matchingAf.fileNumber}`
                                        : `Tekrar Başvuru Bilgileri — Kaza Dosyası #${i + 1}`;
                                    return (
                                        <FilesCollapsible
                                            key={`re-${re.id}`}
                                            title={title}
                                            referenceId={re.id}
                                            canEdit={false}
                                        />
                                    );
                                })}
                            </GroupCollapsible>

                            <GroupCollapsible title="İcralar" count={enforcements.length}>
                                {enforcements.map((e, i) => (
                                    <FilesCollapsible
                                        key={`en-${e.id}`}
                                        title={formatTitle(e.enforcementCaseNumber ? `İcra Esas No: ${e.enforcementCaseNumber}` : null, `İcra #${i + 1} Dosyaları`)}
                                        referenceId={e.id}
                                        canEdit={false}
                                    />
                                ))}
                            </GroupCollapsible>

                            <GroupCollapsible title="Masraflar" count={expenses.length}>
                                {expenses.map((e, i) => (
                                    <FilesCollapsible
                                        key={`ex-${e.id}`}
                                        title={formatTitle(e.expenseNumber ? `Masraf No: ${e.expenseNumber}` : null, `Masraf Faturası #${i + 1}`)}
                                        referenceId={e.id}
                                        typeFilter={["Fatura"]}
                                        canEdit={false}
                                    />
                                ))}
                            </GroupCollapsible>

                            <GroupCollapsible title="Tahsilatlar" count={paymentCollections.length}>
                                {paymentCollections.map((p, i) => (
                                    <FilesCollapsible
                                        key={`pc-${p.id}`}
                                        title={formatTitle(p.paymentCollectionNumber ? `Tahsilat No: ${p.paymentCollectionNumber}` : null, `Tahsilat Faturası #${i + 1}`)}
                                        referenceId={p.id}
                                        typeFilter={["Fatura"]}
                                        canEdit={false}
                                    />
                                ))}
                            </GroupCollapsible>

                            <GroupCollapsible title="Kapaklar" count={finalCompensations.length}>
                                {finalCompensations.map((c, i) => (
                                    <FilesCollapsible
                                        key={`fc-${c.id}`}
                                        title={formatTitle(c.enforcementCaseNumber ? `İcra Dosya No: ${c.enforcementCaseNumber}` : null, `Kapak #${i + 1} Dosyaları`)}
                                        referenceId={c.id}
                                        canEdit={false}
                                    />
                                ))}
                            </GroupCollapsible>

                            {accidentFiles.length === 0 && (
                                <div className="text-muted text-center py-3">Bu kazaya bağlı dosya bulunamadı.</div>
                            )}
                        </>
                    )}
                </CardBody>
            </Collapse>
        </Card>
    );
};

const AccidentsInfo = ({ accidents = [], isLoading = false }) => {
    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <H5>Kazalar</H5>
                <div style={{ height: '38px' }} aria-hidden="true" />
            </div>
            <div style={{ minHeight: '200px' }}>
                {isLoading ? (
                    <div className="py-4 text-center">Yükleniyor...</div>
                ) : accidents.length === 0 ? (
                    <div className="w-100 text-start">Müşteriye ait kaza bulunamadı.</div>
                ) : (
                    accidents.map((accident, idx) => (
                        <AccidentCard key={accident.id} accident={accident} index={idx} />
                    ))
                )}
            </div>
        </>
    );
};

export default AccidentsInfo;
